import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../i18n';
import { adminRequest } from '../../../shared/services/api';

// ═══════════════════════════════════════════════════════════════
// CrearTenantWizardPage — prompt 0518
// Wizard de 3 pasos para crear un nuevo tenant desde el backoffice.
// Accesible solo para SUPERADMIN_PLATFORM (guard en App.tsx).
// POST /api/v1/admin/tenants via adminRequest('/tenants', ...)
// ═══════════════════════════════════════════════════════════════

// ─── Estilos compartidos ────────────────────────────────────────

const CARD: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
  padding: '32px 40px',
  maxWidth: 680,
  width: '100%',
};

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid #D1D5DB',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'DM Sans, sans-serif',
  boxSizing: 'border-box',
  color: '#111827',
  outline: 'none',
};

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const SELECT: React.CSSProperties = {
  ...INPUT,
  backgroundColor: '#fff',
  cursor: 'pointer',
};

const FIELD: React.CSSProperties = { marginBottom: 20 };

const BTN_PRIMARY: React.CSSProperties = {
  padding: '11px 28px',
  backgroundColor: '#7C3AED',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const BTN_SECONDARY: React.CSSProperties = {
  padding: '11px 28px',
  backgroundColor: 'transparent',
  color: '#7C3AED',
  border: '1px solid #7C3AED',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// ─── Tipos ──────────────────────────────────────────────────────

interface TenantForm {
  nombre: string;
  slug: string;
  plan: 'BASIC' | 'PROFESSIONAL' | 'ENTERPRISE' | 'PREMIUM';
  maxUsuarios: number;
  modulosActivos: string[];
  idiomaPredeterminado: string;
  zonaHoraria: string;
  formatoFecha: string;
  colorPrimario: string;
  colorSecundario: string;
  colorAcento: string;
  fuente: string;
}

interface TenantCreado {
  id: string;
  nombre: string;
  slug: string;
}

const MODULOS_DISPONIBLES = [
  { id: 'M1', label: 'M1 — Estructura Organizacional' },
  { id: 'M2', label: 'M2 — Procesos' },
  { id: 'M3', label: 'M3 — Documentos' },
  { id: 'M4', label: 'M4 — Flujogramas' },
  { id: 'M5', label: 'M5 — Búsqueda' },
  { id: 'M6', label: 'M6 — Versiones' },
  { id: 'M7', label: 'M7 — Auditoría' },
  { id: 'M8', label: 'M8 — Configuración' },
  { id: 'M9', label: 'M9 — V·IA Gateway' },
  { id: 'M10', label: 'M10 — V·IA RAG' },
  { id: 'M11', label: 'M11 — KPIs' },
  { id: 'M13', label: 'M13 — V·IAnet News' },
  { id: 'M14', label: 'M14 — Mobile' },
  { id: 'M15', label: 'M15 — V·IAnet Sign' },
  { id: 'M16', label: 'M16 — V·IAnet Requests' },
  { id: 'M17', label: 'M17 — V·IAnet Notify' },
];

const ZONAS_HORARIAS = [
  'Europe/Madrid',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'UTC',
];

const DEFAULT_FORM: TenantForm = {
  nombre: '',
  slug: '',
  plan: 'PROFESSIONAL',
  maxUsuarios: 50,
  modulosActivos: ['M1', 'M2', 'M4', 'M8'],
  idiomaPredeterminado: 'es',
  zonaHoraria: 'Europe/Madrid',
  formatoFecha: 'DD/MM/YYYY',
  colorPrimario: '#1a56db',
  colorSecundario: '#1a56db',
  colorAcento: '#1a56db',
  fuente: 'DM Sans',
};

// ─── Helpers ────────────────────────────────────────────────────

function toSlug(nombre: string): string {
  return nombre
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

// ─── Subcomponentes de pasos ─────────────────────────────────────

function StepIdentificacion({
  form,
  onChange,
}: {
  form: TenantForm;
  onChange: (patch: Partial<TenantForm>) => void;
}) {
  const { t } = useI18n();
  const w = t.backoffice.wizard;

  return (
    <>
      <div style={FIELD}>
        <label style={LABEL}>{w.nombre} *</label>
        <input
          data-testid="wizard-input-nombre"
          style={INPUT}
          value={form.nombre}
          placeholder={w.nombrePlaceholder}
          onChange={(e) => {
            const nombre = e.target.value;
            onChange({ nombre, slug: toSlug(nombre) });
          }}
        />
      </div>

      <div style={FIELD}>
        <label style={LABEL}>Slug *</label>
        <input
          data-testid="wizard-input-slug"
          style={INPUT}
          value={form.slug}
          placeholder="grupo-puentes"
          onChange={(e) => onChange({ slug: e.target.value })}
        />
        <span style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4, display: 'block' }}>
          {w.slugHint}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={FIELD}>
          <label style={LABEL}>{w.plan}</label>
          <select
            data-testid="wizard-select-plan"
            style={SELECT}
            value={form.plan}
            onChange={(e) => onChange({ plan: e.target.value as TenantForm['plan'] })}
          >
            <option value="BASIC">BASIC</option>
            <option value="PROFESSIONAL">PROFESSIONAL</option>
            <option value="ENTERPRISE">ENTERPRISE</option>
            <option value="PREMIUM">PREMIUM</option>
          </select>
        </div>

        <div style={FIELD}>
          <label style={LABEL}>{w.maxUsuarios}</label>
          <input
            style={INPUT}
            type="number"
            min={1}
            max={10000}
            value={form.maxUsuarios}
            onChange={(e) => onChange({ maxUsuarios: parseInt(e.target.value) || 50 })}
          />
        </div>
      </div>
    </>
  );
}

function StepConfiguracion({
  form,
  onChange,
}: {
  form: TenantForm;
  onChange: (patch: Partial<TenantForm>) => void;
}) {
  const { t } = useI18n();
  const w = t.backoffice.wizard;

  const toggleModulo = (id: string) => {
    const current = form.modulosActivos;
    const next = current.includes(id) ? current.filter((m) => m !== id) : [...current, id];
    onChange({ modulosActivos: next });
  };

  return (
    <>
      <div style={FIELD}>
        <label style={LABEL}>{w.modulos}</label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px 16px',
            padding: '12px 16px',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          {MODULOS_DISPONIBLES.map((m) => (
            <label
              key={m.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: '#374151',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={form.modulosActivos.includes(m.id)}
                onChange={() => toggleModulo(m.id)}
                style={{ accentColor: '#7C3AED', width: 15, height: 15, cursor: 'pointer' }}
              />
              {m.label}
            </label>
          ))}
        </div>
        <span style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4, display: 'block' }}>
          {form.modulosActivos.length} {w.modulosSeleccionados}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={FIELD}>
          <label style={LABEL}>{w.idioma}</label>
          <select
            style={SELECT}
            value={form.idiomaPredeterminado}
            onChange={(e) => onChange({ idiomaPredeterminado: e.target.value })}
          >
            <option value="es">Español</option>
            <option value="en">English</option>
            <option value="zh">中文</option>
          </select>
        </div>

        <div style={FIELD}>
          <label style={LABEL}>{w.formatoFecha}</label>
          <select
            style={SELECT}
            value={form.formatoFecha}
            onChange={(e) => onChange({ formatoFecha: e.target.value })}
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>
      </div>

      <div style={FIELD}>
        <label style={LABEL}>{w.zonaHoraria}</label>
        <select
          style={SELECT}
          value={form.zonaHoraria}
          onChange={(e) => onChange({ zonaHoraria: e.target.value })}
        >
          {ZONAS_HORARIAS.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

function StepMarca({
  form,
  onChange,
}: {
  form: TenantForm;
  onChange: (patch: Partial<TenantForm>) => void;
}) {
  const { t } = useI18n();
  const w = t.backoffice.wizard;

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <div style={FIELD}>
          <label style={LABEL}>{w.colorPrimario}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="color"
              value={form.colorPrimario}
              onChange={(e) => onChange({ colorPrimario: e.target.value })}
              style={{
                width: 40,
                height: 38,
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                cursor: 'pointer',
                padding: 2,
              }}
            />
            <input
              style={{ ...INPUT, flex: 1 }}
              value={form.colorPrimario}
              onChange={(e) => onChange({ colorPrimario: e.target.value })}
              placeholder="#1a56db"
            />
          </div>
        </div>

        <div style={FIELD}>
          <label style={LABEL}>{w.colorSecundario}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="color"
              value={form.colorSecundario}
              onChange={(e) => onChange({ colorSecundario: e.target.value })}
              style={{
                width: 40,
                height: 38,
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                cursor: 'pointer',
                padding: 2,
              }}
            />
            <input
              style={{ ...INPUT, flex: 1 }}
              value={form.colorSecundario}
              onChange={(e) => onChange({ colorSecundario: e.target.value })}
              placeholder="#1a56db"
            />
          </div>
        </div>

        <div style={FIELD}>
          <label style={LABEL}>{w.colorAcento}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="color"
              value={form.colorAcento}
              onChange={(e) => onChange({ colorAcento: e.target.value })}
              style={{
                width: 40,
                height: 38,
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                cursor: 'pointer',
                padding: 2,
              }}
            />
            <input
              style={{ ...INPUT, flex: 1 }}
              value={form.colorAcento}
              onChange={(e) => onChange({ colorAcento: e.target.value })}
              placeholder="#1a56db"
            />
          </div>
        </div>
      </div>

      <div style={FIELD}>
        <label style={LABEL}>{w.fuente}</label>
        <select
          style={SELECT}
          value={form.fuente}
          onChange={(e) => onChange({ fuente: e.target.value })}
        >
          <option value="DM Sans">DM Sans</option>
          <option value="Inter">Inter</option>
          <option value="Roboto">Roboto</option>
          <option value="Open Sans">Open Sans</option>
        </select>
      </div>

      {/* Preview */}
      <div
        style={{
          marginTop: 8,
          padding: '16px 20px',
          borderRadius: 8,
          border: '1px solid #E5E7EB',
          backgroundColor: '#FAFAFA',
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#9CA3AF',
            marginBottom: 12,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
          }}
        >
          {w.preview}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div
            style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: form.colorPrimario }}
          />
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              backgroundColor: form.colorSecundario,
            }}
          />
          <div
            style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: form.colorAcento }}
          />
          <span
            style={{
              fontSize: 15,
              fontFamily: `${form.fuente}, sans-serif`,
              color: form.colorPrimario,
              fontWeight: 600,
            }}
          >
            {form.nombre || 'Nombre del tenant'}
          </span>
        </div>
      </div>
    </>
  );
}

// ─── Wizard principal ────────────────────────────────────────────

export function CrearTenantWizardPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const w = t.backoffice.wizard;

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<TenantForm>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creado, setCreado] = useState<TenantCreado | null>(null);

  const TOTAL_STEPS = 3;

  const onChange = (patch: Partial<TenantForm>) => setForm((f) => ({ ...f, ...patch }));

  const canNext = () => {
    if (step === 0) return form.nombre.trim().length > 0 && form.slug.trim().length > 0;
    return true;
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS - 1) {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await adminRequest<{ data: TenantCreado }>('/tenants', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setCreado(response.data);
      setStep(TOTAL_STEPS); // success step
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const STEP_LABELS = [w.step0Label, w.step1Label, w.step2Label];

  // ─── Pantalla de éxito ──────────────────────────────────────────
  if (step === TOTAL_STEPS && creado) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F5F3FF',
          padding: '48px 24px',
          fontFamily: 'DM Sans, sans-serif',
        }}
      >
        <div style={{ ...CARD, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1F3864', marginBottom: 8 }}>
            {w.exitoTitulo}
          </h2>
          <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>{w.exitoDescripcion}</p>
          <p
            data-testid="wizard-success-line"
            style={{ fontSize: 16, fontWeight: 600, color: '#7C3AED', marginBottom: 32 }}
          >
            {creado.nombre} · {creado.slug}
          </p>
          <button
            data-testid="wizard-btn-volver-backoffice"
            style={BTN_PRIMARY}
            onClick={() => navigate('/backoffice')}
          >
            {w.volverBackoffice}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#F5F3FF',
        fontFamily: 'DM Sans, sans-serif',
        padding: '48px 24px',
      }}
    >
      {/* Header */}
      <div style={{ maxWidth: 680, width: '100%', marginBottom: 24 }}>
        <button
          onClick={() => navigate('/backoffice')}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 13,
            color: '#7C3AED',
            cursor: 'pointer',
            padding: 0,
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginBottom: 16,
          }}
        >
          ← {w.volverBackoffice}
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1F3864', margin: 0 }}>{w.titulo}</h1>
        <p style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>{w.subtitulo}</p>
      </div>

      {/* Stepper */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          marginBottom: 32,
          maxWidth: 680,
          width: '100%',
        }}
      >
        {STEP_LABELS.map((label, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: i < STEP_LABELS.length - 1 ? 1 : 'none',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: i <= step ? '#7C3AED' : '#E5E7EB',
                  color: i <= step ? '#fff' : '#9CA3AF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  transition: 'background-color 0.2s',
                }}
              >
                {i < step ? '✓' : i + 1}
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: i === step ? 600 : 400,
                  color: i === step ? '#7C3AED' : '#9CA3AF',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  backgroundColor: i < step ? '#7C3AED' : '#E5E7EB',
                  margin: '0 8px',
                  marginTop: -12,
                  transition: 'background-color 0.2s',
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Card */}
      <div style={CARD}>
        <h2
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: '#1F3864',
            marginBottom: 24,
            marginTop: 0,
          }}
        >
          {STEP_LABELS[step]}
        </h2>

        {step === 0 && <StepIdentificacion form={form} onChange={onChange} />}
        {step === 1 && <StepConfiguracion form={form} onChange={onChange} />}
        {step === 2 && <StepMarca form={form} onChange={onChange} />}

        {/* Error inline */}
        {error && (
          <div
            data-testid="wizard-error-inline"
            style={{
              marginBottom: 20,
              padding: '10px 16px',
              borderRadius: 8,
              fontSize: 13,
              backgroundColor: '#FEF2F2',
              color: '#DC2626',
              border: '1px solid #FECACA',
            }}
          >
            {error}
          </div>
        )}

        {/* Navegación */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          {step > 0 ? (
            <button
              data-testid="wizard-btn-anterior"
              style={BTN_SECONDARY}
              onClick={handleBack}
              disabled={submitting}
            >
              ← {w.anterior}
            </button>
          ) : (
            <div />
          )}

          {step < TOTAL_STEPS - 1 ? (
            <button
              data-testid="wizard-btn-siguiente"
              style={{ ...BTN_PRIMARY, opacity: canNext() ? 1 : 0.5 }}
              onClick={handleNext}
              disabled={!canNext()}
            >
              {w.siguiente} →
            </button>
          ) : (
            <button
              data-testid="wizard-btn-crear"
              style={{ ...BTN_PRIMARY, opacity: submitting ? 0.7 : 1 }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? w.creando : w.crear}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
